'use client';

import { useMemo, useRef, useEffect, useState, useCallback } from 'react';
import { usePathname } from 'next/navigation';
import { gsap } from 'gsap';
import { Link, Text, useTranslation } from '@weareconceptstudio/core';
import { useUser } from '@weareconceptstudio/account';

//* HOC's
import { config } from '@/helpers';
import { withUIContext } from '@/context';

//* Style
import BurgerMenuStyle from './style';

const BurgerMenu = ({ menuBar, isTouchScreen, winWidth }) => {
	const { prevRoute } = useUser();
	const { selectedLang } = useTranslation();

	//! Refs
	const containerRef = useRef(null);
	const burgerMenuItemsRef = useRef(null);
	const burgerMenuButtonRef = useRef(null);
	const burgerMenuTextBlock = useRef(null);
	const navigation = useRef(null);
	const svgRef = useRef();
	const svgBlockRef = useRef();
	const cursorRef = useRef();
	const tlHoverButton = useRef();
	const svgHoverTimeline = useRef();
	const svgTextTimeline = useRef();
	const timeLineNav = useRef();
	const timeLineCursor = useRef();
	const styleWrapperRef = useRef();
	const fakeRef = useRef();
	const wrapperButton = useRef();
	const timeLineFirstLoad = useRef(null);

	//! States
	const [menuOpen, setMenuOpen] = useState(false);
	const [activeItem, setActiveItem] = useState(0);
	const [hoverMouseMove, setHoverMouseMove] = useState(false);
	const [firstLoad, setFirstLoad] = useState(true);

	//! Size State
	const [buttonRealWidth, setButtonRealWidth] = useState();
	const [openedSize, setOpenedSize] = useState();

	const menu_bar = useMemo(() => {
		return [...menuBar, { name: config.routes.home.name, slug: config.routes.home.path }];
	}, [menuBar]);

	//! Checks
	const isDesktop = useMemo(() => {
		return !isTouchScreen;
	}, [isTouchScreen, winWidth]);

	//! Navigation
	let pathname = usePathname();

	pathname = pathname.replace(selectedLang + '/', '');
	pathname = pathname.split('/')[1];

	const useBurgerMenuHide = useMemo(() => {
		return [
			'sign-in',
			'sign-up',
			'forgot-password',
			'reset-password',
			'verify-phone',
			'thank-you-message',
			'payment-failed',
			'account',
			'cart',
			'checkout',
			'menu',
		].includes(pathname);
	}, [pathname]);

	//! Burger Menu Items
	const burgerMenuItems = useMemo(() => {
		return menu_bar.map((route, index) => {
			return (
				<Link
					key={index}
					content={route.name}
					ariaLabel={route.name}
					className={`burger-menu-item-link ellipsis`}
					url={route.slug == '/' ? route.slug : `${config.routes.menu.path}${route.slug}`}
				/>
			);
		});
	}, [menu_bar]);

	//! First Load
	useEffect(() => {
		let timeoutId;

		if (useBurgerMenuHide || !buttonRealWidth) return;

		if (
			firstLoad ||
			prevRoute.includes('sign-in') ||
			prevRoute.includes('sign-up') ||
			prevRoute.includes('forgot-password') ||
			prevRoute.includes('reset-password') ||
			prevRoute.includes('verify-phone') ||
			prevRoute.includes('thank-you-message') ||
			prevRoute.includes('payment-failed') ||
			prevRoute.includes('account') ||
			prevRoute.includes('cart') ||
			prevRoute.includes('checkout') ||
			prevRoute.includes('menu')
		) {
			const runAnimation = () => {
				const prevWidth = burgerMenuButtonRef.current.clientWidth;

				burgerMenuButtonRef.current.classList.remove('first-load-burger-menu');

				timeLineFirstLoad.current = gsap.timeline();

				timeLineFirstLoad.current.fromTo(
					burgerMenuButtonRef.current,
					{ scale: 0 },
					{ scale: 1, duration: 0.4, ease: 'power1.inOut' }
				);

				gsap.to(burgerMenuTextBlock.current, {
					delay: 0.6,
					opacity: 1,
					duration: 0.2,
				});

				timeLineFirstLoad.current
					.fromTo(
						burgerMenuButtonRef.current,
						{ width: prevWidth },
						{ width: buttonRealWidth + 8, duration: 0.4, ease: 'power2.out' }
					)
					.to(burgerMenuButtonRef.current, {
						width: buttonRealWidth,
						duration: 0.3,
						ease: 'power2.out',
					})
					.then(() => {
						setFirstLoad(false);
					});
			};

			timeoutId = setTimeout(runAnimation, 1000);
		}

		return () => {
			clearTimeout(timeoutId);

			if (timeLineFirstLoad.current) {
				timeLineFirstLoad.current.kill();
			}
		};
	}, [useBurgerMenuHide, prevRoute, buttonRealWidth]);

	//! Size to transform
	useEffect(() => {
		if (!useBurgerMenuHide) {
			setButtonRealWidth(wrapperButton.current.clientWidth);
			setOpenedSize(fakeRef.current.clientWidth);
		}
	}, [useBurgerMenuHide]);

	//! Animation
	useEffect(() => {
		if (!useBurgerMenuHide) {
			if (!firstLoad) {
				if ((!menuOpen || !isDesktop) && hoverMouseMove) {
					const paddingButton = parseInt(window.getComputedStyle(burgerMenuButtonRef?.current).paddingBlock);

					const svgBlockRefSize = svgBlockRef.current.clientWidth;
					const translateSvg = buttonRealWidth / 2 - svgBlockRefSize / 2 - paddingButton;

					if (tlHoverButton.current) tlHoverButton.current.kill();

					tlHoverButton.current = gsap.timeline();

					if (isDesktop) {
						tlHoverButton.current.to(burgerMenuButtonRef.current, {
							width: buttonRealWidth + 16,
							ease: 'power2.out',
							duration: 0.3,
						});
						tlHoverButton.current.to(burgerMenuButtonRef.current, {
							width: buttonRealWidth,
							duration: 0.3,
							ease: 'power2.out',
						});
					} else {
						if (menuOpen) {
							tlHoverButton.current.to(burgerMenuButtonRef.current, {
								width: buttonRealWidth + 16,
								ease: 'power2.out',
								duration: 0.3,
							});
							tlHoverButton.current.to(burgerMenuButtonRef.current, {
								width: buttonRealWidth,
								duration: 0.2,
								ease: 'power2.out',
							});
						}
					}

					svgHoverTimeline.current = gsap.timeline();
					svgHoverTimeline.current.to(svgBlockRef.current, {
						x: `${translateSvg + paddingButton + 4}`,
						rotate: 110,
						duration: 0.3,
						ease: 'power2.out',
					});
					svgHoverTimeline.current.to(svgBlockRef.current, {
						x: translateSvg,
						rotate: 90,
						duration: 0.3,
						ease: 'power2.out',
					});
					svgTextTimeline.current = gsap.timeline();
					svgTextTimeline.current.to(burgerMenuTextBlock.current, {
						opacity: 0,
						duration: 0.3,
						ease: 'power2.out',
					});
				}

				if (menuOpen) {
					gsap.to(burgerMenuButtonRef.current, {
						width: buttonRealWidth,
						duration: 0.05,
						ease: 'power2.out',
					});
				}
			}
		}

		return () => {
			gsap.to(burgerMenuButtonRef.current, { width: buttonRealWidth, duration: 0, ease: 'power2.out' });

			if (tlHoverButton.current) tlHoverButton.current.kill();
			if (svgHoverTimeline.current) svgHoverTimeline.current.kill();
		};
	}, [hoverMouseMove, menuOpen, buttonRealWidth, activeItem, firstLoad, useBurgerMenuHide]);

	//! Control Menu Open Close Animation
	useEffect(() => {
		if (!useBurgerMenuHide) {
			if (!firstLoad) {
				const navigationMenuHeight = burgerMenuItemsRef.current.clientHeight;
				const buttonHeight = burgerMenuButtonRef.current.clientHeight;
				const itemNavHeight = burgerMenuItemsRef.current.children[0].clientHeight;
				const svgBlockRefWidth = svgBlockRef.current.clientWidth;
				const cursorPadT = parseInt(window.getComputedStyle(cursorRef.current).paddingBlockStart);
				const itemPadT = parseInt(
					window.getComputedStyle(burgerMenuItemsRef.current.children[0]).paddingBlockStart
				);
				const deltaPad = cursorPadT - itemPadT;

				if (menuOpen) {
					if (!isDesktop) setHoverMouseMove(true);

					gsap.to(burgerMenuItemsRef.current, { opacity: 1, duration: 0 });

					//! Navigation Menu
					timeLineNav.current = gsap.timeline();
					timeLineNav.current.to(navigation.current, {
						pointerEvents: 'auto',
						duration: 0,
						zIndex: 2,
						delay: `${!isDesktop ? 0.6 : 0}`,
					});
					timeLineNav.current.to(navigation.current, { opacity: 1, duration: 0 });
					timeLineNav.current.to(navigation.current, {
						height: navigationMenuHeight + 8,
						width: openedSize + 8,
						borderRadius: 24,
						ease: 'power2.out',
						duration: 0.3,
					});
					timeLineNav.current.to(navigation.current, {
						height: navigationMenuHeight,
						width: openedSize,
						duration: 0.2,
						ease: 'power2.out',
					});

					//! Cursor
					timeLineCursor.current = gsap.timeline();
					timeLineCursor.current.to(cursorRef.current, {
						duration: 0.3,
						height: `${itemNavHeight + deltaPad * 2}px`,
						ease: 'power2.out',
					});
					timeLineCursor.current.to(cursorRef.current, {
						duration: 0.1,
						height: `${itemNavHeight + deltaPad * 2}px`,
						ease: 'power2.out',
					});
				} else {
					gsap.to(burgerMenuItemsRef.current, { duration: 0 });

					//! Kill prev
					if (timeLineNav.current) timeLineNav.current.kill();
					if (timeLineCursor.current) timeLineCursor.current.kill();
					if (svgHoverTimeline.current) svgHoverTimeline.current.kill();
					if (svgTextTimeline.current) svgTextTimeline.current.kill();

					//! Navigation
					timeLineNav.current = gsap.timeline();
					timeLineNav.current.to(navigation.current, {
						delay: isDesktop ? 0 : activeItem * 0.1,
						pointerEvents: 'none',
						duration: 0,
						zIndex: 0,
					});
					timeLineNav.current.to(navigation.current, {
						height: buttonHeight,
						width: buttonRealWidth,
						borderRadius: 32,
						ease: 'power2.out',
						duration: 0.3,
						opacity: 0,
					});

					//! Cursor
					timeLineCursor.current = gsap.timeline();
					timeLineCursor.current.to(cursorRef.current, {
						duration: 0.3,
						width: `${svgBlockRefWidth}px`,
						height: `${itemNavHeight + deltaPad * 2}px`,
						ease: 'power2.out',
					});

					//! Svg Hover MouseMove
					gsap.to(svgBlockRef.current, {
						delay: isDesktop ? 0 : activeItem * 0.1,
						x: 0,
						rotate: 0,
						duration: 0.3,
						ease: 'power2.out',
					});
					gsap.to(burgerMenuTextBlock.current, {
						delay: isDesktop ? 0 : activeItem * 0.1,
						opacity: 1,
						duration: 0.3,
						ease: 'power2.out',
					});
				}
			}
		}

		return () => {
			if (timeLineNav.current) timeLineNav.current.kill();
			if (timeLineCursor.current) timeLineCursor.current.kill();
		};
	}, [menuOpen, useBurgerMenuHide, buttonRealWidth, openedSize, isDesktop ? null : activeItem]);

	//! Control open-close Navigation
	const openMenuClick = useCallback(
		(e) => {
			if (!firstLoad) {
				setMenuOpen(!menuOpen);
			}
		},
		[menuOpen, firstLoad]
	);

	//! Mouse Move
	const mouseMove = useCallback(
		(e) => {
			if (!firstLoad) {
				const navigationPadT = parseInt(window.getComputedStyle(burgerMenuItemsRef.current).paddingBlockStart);
				const navigationPadB = parseInt(window.getComputedStyle(burgerMenuItemsRef.current).paddingBlockEnd);
				const heightItem =
					burgerMenuItemsRef.current.getBoundingClientRect().height - navigationPadT - navigationPadB;
				let { y } = burgerMenuButtonRef.current.getBoundingClientRect();
				const burgerMenuItemHeight = heightItem / menu_bar.length;
				let topSize = y + burgerMenuButtonRef.current.clientHeight;
				let delta = topSize - e.clientY - navigationPadB + burgerMenuItemHeight;
				let activeDelta = Math.floor(delta / burgerMenuItemHeight);

				if (activeDelta - 1 < menu_bar.length && activeDelta - 1 >= 0) {
					setActiveItem(activeDelta - 1);
				}

				setHoverMouseMove(true);
			}
		},
		[menuOpen, buttonRealWidth, firstLoad]
	);

	//! Mouse Leave
	const mouseLeave = useCallback(() => {
		if (!firstLoad) {
			if (svgHoverTimeline.current) svgHoverTimeline.current.kill();

			setMenuOpen(false);
			setHoverMouseMove(false);

			gsap.to(burgerMenuButtonRef.current, { width: buttonRealWidth, duration: 0.3, ease: 'power2.out', x: 0 });
			gsap.to(svgBlockRef.current, {
				x: 0,
				rotate: 0,
				duration: 0.2,
				ease: 'power2.out',
				delay: `${menuOpen ? 0.1 : 0}`,
			});
			gsap.to(burgerMenuTextBlock.current, { opacity: 1, delay: 0.1, duration: 0.3, ease: 'power2.out' });
		}
	}, [menuOpen, buttonRealWidth, firstLoad]);

	//! Active Item Navigation Hover
	useEffect(() => {
		if (!useBurgerMenuHide) {
			if (!firstLoad) {
				let translateSize = cursorRef.current.clientHeight;
				let sizeItem = burgerMenuItemsRef.current.children[0].clientHeight;
				let delta = translateSize - sizeItem;

				gsap.to(cursorRef.current, {
					y: `${(-translateSize + delta) * activeItem}px`,
					duration: 0.3,
					ease: 'power2.out',
				});

				[...burgerMenuItemsRef.current.children].map((item, i, arr) => {
					if (arr.length - i - 2 == activeItem) {
						item.classList.add('active');
					} else {
						item.classList.remove('active');
					}
				});
			}
		}
	}, [activeItem, firstLoad, useBurgerMenuHide]);

	return useBurgerMenuHide ? null : (
		<BurgerMenuStyle
			id={'burger-menu-visible'}
			ref={styleWrapperRef}>
			<div
				ref={fakeRef}
				className={'wrapper-fake-size'}
			/>
			<div
				ref={containerRef}
				onClick={openMenuClick}
				onMouseMove={mouseMove}
				onMouseLeave={mouseLeave}
				className='wrapper-button-nav'>
				<nav
					ref={navigation}
					className='burger-menu-items-block'>
					<div
						ref={burgerMenuItemsRef}
						className='burger-menu-items'>
						{burgerMenuItems}
						<span
							ref={cursorRef}
							className='burger-menu-item-cursor'>
							&nbsp;
						</span>
					</div>
				</nav>

				<button
					ref={burgerMenuButtonRef}
					type='button'
					className='burger-menu-button first-load-burger-menu transition-first-load'>
					<div
						className='wrapper-button'
						ref={wrapperButton}
					/>
					<div
						ref={svgBlockRef}
						className='burger-menu-svg-block'>
						<svg
							ref={svgRef}
							fill='none'
							viewBox='0 0 24 24'
							className='burger-menu-svg'
							xmlns='http://www.w3.org/2000/svg'>
							<path
								strokeWidth='3.5'
								d='M0 8H24M0 17H24'
							/>
						</svg>
					</div>

					<div
						ref={burgerMenuTextBlock}
						className='burger-menu-text-block opacity-zero'>
						<Text
							tag={'span'}
							className={'burger-menu-text'}
							text={'menu'}
						/>
					</div>
				</button>
			</div>
		</BurgerMenuStyle>
	);
};

export default withUIContext(BurgerMenu, ['isTouchScreen', 'winWidth']);
